// A Card is the rendering component for a note.
// The Generic Card type is a wrapper for the other types of cards, which are specific to the type of note.

import { forwardRef, useImperativeHandle, useRef, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';
import { Note, Source, Annote, Takeaway, isAnnote } from '@annote/types';
import { colors, text } from '@annote/core/styles';
import { useDragging } from 'src/context/DraggingContext';
import { FocusContext } from 'src/context/FocusContext';
import TakeawayCard from './byType/takeaway/Takeaway';
import { AnnoteCard } from './byType/annote/Annote';
import { SourceCard } from './byType/source/Source';
import { CardButtonsBar } from './menus/CardButtonBar';
import { PinnedIndicator } from './menus/PinnedIndicator';

export interface CardProps {
  note: Note;
  onClick?: (id: string) => void;
  readOnly?: boolean;
}

const getBackgroundColor = (props: {
  isDragging: boolean;
  isSuggestion?: boolean;
  isDeletionSuggestion?: boolean;
}): string => {
  if (props.isDragging) return colors.bg.dragging;
  if (props.isDeletionSuggestion) return colors.bg.suggestionDelete.primary;
  if (props.isSuggestion) return colors.bg.suggestion.primary;
  return colors.bg.primary;
};

const CardOutline = styled.div<{
  isDragging: boolean;
  isOverLinkDropzone?: boolean;
  isFocused?: boolean;
  isSuggestion?: boolean;
  isDeletionSuggestion?: boolean;
}>`
  cursor: pointer;
  position: relative;
  background-color: ${getBackgroundColor};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transform: ${(props) => (props.isSuggestion || props.isDeletionSuggestion ? 'rotate(-2deg)' : 'none')};
  outline: 2px solid transparent;
  z-index: 11;
  transition: transform 0.5s ease;
  width: fit-content;
  &:hover {
    box-shadow: ${colors.shadow.card.hover};
  }
  box-shadow: ${colors.shadow.card.default};
  font-family: ${text.family.primary};

  // highlight blue when
  //  - dragged note is over a dropzone that would link to this note
  //  - note is marked as focused
  ${(props) =>
    (props.isOverLinkDropzone || props.isFocused) &&
    `outline: 1px solid ${colors.lines.selected};  
    box-shadow: ${colors.shadow.card.hover};
  `}

  // Hide NumberOfAnnotes when in suggestion mode
  ${(props) =>
    props.isSuggestion &&
    `
    [data-number-of-annotes] {
      display: none;
    }
  `}
`;

// Get drag type based on note type for better drop filtering
const getDragType = (note: Note): string => {
  switch (note.type) {
    case 'takeaway':
      return 'TAKEAWAY';
    case 'source':
      return 'SOURCE';
    case 'annote':
      return 'ANNOTE';
    default:
      return 'CARD';
  }
};

export const Card = forwardRef<HTMLDivElement, CardProps>(({ note, readOnly = false }, forwardedRef) => {
  const internalRef = useRef<HTMLDivElement | null>(null);
  const { setDraggingNoteKey, draggingNoteKey, hoveringDropzoneKey } = useDragging();
  const { focusedNoteId } = useContext(FocusContext);
  const key = `card-${note.id}`;
  const [{ isDragging }, dragRef] = useDrag({
    type: getDragType(note),
    item: note,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (isDragging) {
      setDraggingNoteKey(key);
    } else if (draggingNoteKey && draggingNoteKey === key) {
      setDraggingNoteKey(null);
    }
  }, [isDragging, setDraggingNoteKey, draggingNoteKey, note.id]);

  useImperativeHandle(forwardedRef, () => internalRef.current as HTMLDivElement);

  // For annote cards, apply dragRef to the entire card
  // For takeaway and source cards, pass dragRef to the component to handle internally
  if (isAnnote(note)) {
    dragRef(internalRef);
  }

  const isOverLinkDropzone = useMemo(
    () => hoveringDropzoneKey?.slice(0, 36) === note.id,
    [hoveringDropzoneKey, note.id],
  );

  const isDeletionSuggestion = !!note.suggestionId && !!note.syncedAt;

  return (
    <CardOutline
      ref={internalRef}
      id={key}
      className="card"
      data-testid="note"
      data-note-type={note.type}
      data-suggested={!!note.suggestionId}
      isDragging={isDragging}
      isOverLinkDropzone={isOverLinkDropzone}
      isFocused={!isDragging && note.id === focusedNoteId}
      isSuggestion={!!note.suggestionId}
      isDeletionSuggestion={isDeletionSuggestion}
    >
      <PinnedIndicator note={note} />
      <CardButtonsBar note={note} />
      {note.type === 'takeaway' && <TakeawayCard takeaway={note as Takeaway} dragRef={dragRef} readOnly={readOnly} />}
      {note.type === 'source' && <SourceCard source={note as Source} dragRef={dragRef} readOnly={readOnly} />}
      {note.type === 'annote' && <AnnoteCard annote={note as Annote} />}
    </CardOutline>
  );
});

export default Card;
